// For Little Saints — review carousel controls and scroll animations
(() => {
  const step = (button, direction) => {
    const track = button.closest('[data-fls-carousel]')?.querySelector('[data-fls-track]');
    if (!track) return;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    const atStart = track.scrollLeft <= 2;
    if (direction > 0 && atEnd) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction < 0 && atStart) {
      track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
    } else {
      track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
    }
  };

  document.addEventListener('click', (event) => {
    const prev = event.target.closest('[data-fls-prev]');
    const next = event.target.closest('[data-fls-next]');
    if (prev) step(prev, -1);
    if (next) step(next, 1);
  });

  // Bring the selected review into view while editing in the theme editor.
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-fls-slide]');
    slide?.parentElement?.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  });

  // Buy more, save more: tier tiles drive the product quantity selector and follow its changes.
  const quantityFor = (tiers) =>
    tiers.closest('.shopify-section')?.querySelector('quantity-selector-component:not([data-cart-line])');

  const syncTiers = (root = document) => {
    root.querySelectorAll('[data-fls-tiers]').forEach((tiers) => {
      const qty = parseInt(quantityFor(tiers)?.querySelector('input')?.value, 10) || 1;
      const tiles = [...tiers.querySelectorAll('[data-fls-tier-qty]')];
      const active = tiles.filter((tile) => qty >= parseInt(tile.dataset.flsTierQty, 10)).pop();
      tiles.forEach((tile) => tile.setAttribute('aria-pressed', String(tile === active)));
    });
  };

  document.addEventListener('click', (event) => {
    const tile = event.target.closest('[data-fls-tier-qty]');
    if (tile) {
      const selector = quantityFor(tile.closest('[data-fls-tiers]'));
      const input = selector?.querySelector('input');
      if (input) {
        const max = parseInt(input.max, 10);
        const qty = parseInt(tile.dataset.flsTierQty, 10);
        input.value = String(max ? Math.min(qty, max) : qty);
        if (typeof selector.onQuantityChange === 'function') {
          selector.onQuantityChange();
          selector.updateButtonStates?.();
        } else {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      syncTiers();
      return;
    }
    if (event.target.closest('quantity-selector-component')) requestAnimationFrame(() => syncTiers());
  });
  document.addEventListener('focusout', (event) => {
    if (event.target.closest?.('quantity-selector-component')) requestAnimationFrame(() => syncTiers());
  });

  // "View more" lists: reveal the rest of the items, then retire the button.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-fls-more]');
    if (!button) return;
    const section = button.closest('.shopify-section') || document;
    section.querySelectorAll('[data-fls-more-list] > [hidden]').forEach((item) => {
      item.hidden = false;
      item.classList.remove('fls-press__item--hidden');
    });
    button.closest('.fls-press__more')?.remove();
  });

  // Review slider: one slide per view, arrows wrap around, dots follow the visible slide.
  const sliderStep = (track) => {
    const slides = track.querySelectorAll('[data-fls-slider-slide]');
    if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;
    return slides[0]?.offsetWidth || track.clientWidth;
  };

  const sliderIndex = (track) => Math.round(track.scrollLeft / Math.max(sliderStep(track), 1));

  const sliderGo = (slider, index) => {
    const track = slider.querySelector('[data-fls-slider-track]');
    const count = track.querySelectorAll('[data-fls-slider-slide]').length;
    if (!count) return;
    const step = sliderStep(track);
    const lastStart = Math.max(Math.ceil((track.scrollWidth - track.clientWidth) / step), 0);
    let next = index;
    if (next < 0) next = lastStart;
    if (next > lastStart) next = 0;
    track.scrollTo({ left: next * step, behavior: 'smooth' });
  };

  const sliderSync = (slider) => {
    const track = slider.querySelector('[data-fls-slider-track]');
    const current = sliderIndex(track);
    slider.querySelectorAll('[data-fls-slider-dots] button').forEach((dot, i) => {
      if (i === current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  document.addEventListener('click', (event) => {
    const slider = event.target.closest('[data-fls-slider]');
    if (!slider) return;
    const track = slider.querySelector('[data-fls-slider-track]');
    if (event.target.closest('[data-fls-slider-prev]')) sliderGo(slider, sliderIndex(track) - 1);
    if (event.target.closest('[data-fls-slider-next]')) sliderGo(slider, sliderIndex(track) + 1);
    const dot = event.target.closest('[data-fls-slider-dots] button');
    if (dot) sliderGo(slider, [...dot.parentElement.children].indexOf(dot));
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target.closest?.('[data-fls-slider-track]');
      if (track) sliderSync(track.closest('[data-fls-slider]'));
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    const track = event.target.closest?.('[data-fls-slider-track]');
    if (!track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    sliderGo(track.closest('[data-fls-slider]'), sliderIndex(track) + (event.key === 'ArrowRight' ? 1 : -1));
  });

  // One steady height for the whole slider, so it never jumps between reviews
  const sliderMeasure = (slider) => {
    const track = slider.querySelector('[data-fls-slider-track]');
    const slides = [...track.querySelectorAll('[data-fls-slider-slide]')];
    if (!slides.length) return;
    track.style.height = 'auto';
    const tallest = Math.max(...slides.map((slide) => slide.offsetHeight));
    track.style.height = `${tallest}px`;
    sliderSync(slider);
  };

  const sliderSetupAll = () => document.querySelectorAll('[data-fls-slider]').forEach(sliderMeasure);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sliderSetupAll);
  else sliderSetupAll();
  window.addEventListener('load', sliderSetupAll);
  window.addEventListener('resize', sliderSetupAll);
  document.addEventListener('shopify:section:load', sliderSetupAll);

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-fls-slider-slide]');
    const slider = slide?.closest('[data-fls-slider]');
    if (slider) sliderGo(slider, [...slide.parentElement.children].indexOf(slide));
  });

  // Scroll animations. Content stays visible without JS and for visitors who prefer reduced motion.
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || !('IntersectionObserver' in window)) return;

  const REVEAL = [
    '.fls-feature__media',
    '.fls-feature__content',
    '.fls-collage__content',
    '.fls-products__header',
    '.fls-card',
    '.fls-statement__heading',
    '.fls-statement__text',
    '.fls-statement__stat',
    '.fls-statement__buttons',
    '.fls-statement__image',
    '.fls-follow__tile',
    '.fls-follow__cta',
    '.fls-image__inner',
    '.fls-faq__item',
    '.fls-intro__lead',
    '.fls-intro__body',
    '.fls-cards__header',
    '.fls-cards__media',
    '.fls-cards__card',
    '.fls-book__media',
    '.fls-book__content',
    '.fls-contact__panel',
    '.fls-contact__form-wrap',
    '.fls-image-row__item',
    '.fls-banner__copy',
    '.fls-footer__community-inner',
    '.fls-reviews__header',
    '.fls-reviews__slider',
  ].join(',');

  const countUp = (el) => {
    const target = parseFloat(el.dataset.flsCount);
    const decimals = (el.dataset.flsCount.split('.')[1] || '').length;
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.firstChild.nodeValue = (target * eased).toFixed(decimals);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.classList.add('is-visible');
        if (el.dataset.flsCount) countUp(el);
        observer.unobserve(el);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );

  const setup = (root) => {
    root.querySelectorAll(REVEAL).forEach((el) => {
      if (el.hasAttribute('data-fls-reveal')) return;
      const siblings = [...el.parentElement.children].filter((child) => child.matches(REVEAL));
      el.style.setProperty('--fls-delay', `${Math.min(siblings.indexOf(el), 5) * 90}ms`);
      el.setAttribute('data-fls-reveal', '');
      observer.observe(el);
    });

    root.querySelectorAll('.fls-hero__brush, .fls-statement__brush').forEach((el) => {
      if (el.hasAttribute('data-fls-brush')) return;
      el.setAttribute('data-fls-brush', '');
      observer.observe(el);
    });

    root.querySelectorAll('.fls-statement__number').forEach((el) => {
      const text = el.firstChild;
      if (el.dataset.flsCount || !text || text.nodeType !== Node.TEXT_NODE) return;
      const value = text.nodeValue.trim();
      if (!/^\d+(\.\d+)?$/.test(value)) return;
      el.dataset.flsCount = value;
      text.nodeValue = '0';
      observer.observe(el);
    });
  };

  // Sections re-rendered by the theme editor get fresh elements, so animate them again.
  document.addEventListener('shopify:section:load', (event) => setup(event.target));

  const start = () => setup(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
